import { useState } from "react";
import {
  parseShopSections,
  SHOP_SECTION_ITEMS,
  SHOP_SECTION_NEWEST_DAYS,
  SHOP_SECTION_TITLE_MAX,
  SHOP_SECTION_TYPES,
  SHOP_SECTIONS_MAX,
  type ShopBannerLink,
  type ShopBannerTarget,
  type ShopSection,
  type ShopSectionType,
  type ShopSectionView,
  ANNOUNCEABLE_SCOPES,
  argentinaDate,
  isShopAccent,
  parseShopLink,
  PLAN_PERKS,
  SHOP_ABOUT_MAX,
  SHOP_ACCENTS,
  SHOP_ANNOUNCEMENT_MAX,
  SHOP_LINKS_MAX,
  SHOP_TAGLINE_MAX,
  type ListingCard,
  type SellerPublic,
  type ShopAccent,
  type AnnounceableScope,
  type ShopAnnouncement,
  type ShopContent,
  type ShopLink,
} from "../../shared/market";
import { useI18n } from "../i18n/I18nProvider";
import { ApiError, imageUrl } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi, type MyListing, type MySeller } from "./api";
import { Dialog, useArs } from "./components";
import { useCouponOff } from "./CouponsTab";
import type { SellerCoupon } from "../../shared/marketCoupons";
import { ShopView } from "./ShopView";
import { ShopShowcase } from "./Showcase";
import { useGame } from "../lib/GameProvider";

/** A coupon the page can announce now: live, started, not ended, with uses left (the server's rule too). */
const couponWorksNow = (c: SellerCoupon, now = Date.now()) =>
  c.status === "active" && Date.parse(c.startsAt) <= now && (c.endsAt === null || Date.parse(c.endsAt) > now) && (c.usesMax === null || c.uses < c.usesMax);

const EMPTY_SHOP: ShopContent = { banner: null, about: "", links: [], logo: null, tagline: "", accent: null };

/**
 * The customized shop (plan perk): banner, logo, a line under the name, a color, "Sobre la tienda",
 * social links and the listings pinned on top, with a preview before saving. Other plans see what it
 * is and which plan has it.
 */
export function ShopEditor({ seller, onSaved }: { seller: MySeller; onSaved: (s: MySeller) => void }) {
  const { t, locale } = useI18n();
  const couponOff = useCouponOff();
  const shop = seller.shop ?? EMPTY_SHOP;
  const [about, setAbout] = useState(shop.about);
  const [tagline, setTagline] = useState(shop.tagline);
  const [accent, setAccent] = useState<ShopAccent | null>(shop.accent);
  const [links, setLinks] = useState(() => [...shop.links.map((l) => l.url), "", "", "", ""].slice(0, SHOP_LINKS_MAX));
  const [sections, setSections] = useState<ShopSection[]>(seller.sections ?? []);
  const [annText, setAnnText] = useState(seller.announcement?.text ?? "");
  const [annCoupon, setAnnCoupon] = useState<number | null>(seller.announcement?.couponId ?? null);
  const [annUntil, setAnnUntil] = useState(seller.announcement?.until ?? "");
  const [bannerLink, setBannerLink] = useState<ShopBannerLink | null>(seller.bannerLink ?? null);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error" | "links" | "blocked" | "announcement" | "sections" | "bannerLink">("idle");
  const allowed = seller.plan ? PLAN_PERKS[seller.plan.name].customShop : false;
  const maxPins = seller.plan ? PLAN_PERKS[seller.plan.name].shopPins : 0;
  // The page as buyers see it (the base of the preview), and the pins: every id (paused ones too) and the live cards.
  const { game } = useGame();
  const pub = useAsync((signal) => (allowed ? marketApi.seller(seller.handle, signal, game) : Promise.resolve(null)), [allowed, seller.handle, game]);
  const saved = useAsync((signal) => (maxPins > 0 ? marketApi.shopPins(signal) : Promise.resolve(null)), [maxPins]);
  const maxSections = seller.plan ? PLAN_PERKS[seller.plan.name].shopSections : 0;
  // What each section would show now: asked again when what it shows changes, not on every letter of a
  // title (checked by the server's rules, with any title; the preview dialog shows the real ones).
  const sectionQuery = parseShopSections(sections.map((x) => ({ ...x, title: "--" })));
  const sectionsKey = JSON.stringify(sectionQuery);
  const sectionPreviews = useAsync((signal) => (maxSections > 0 && sectionQuery?.length ? marketApi.previewSections(sectionQuery, signal) : Promise.resolve(null)), [maxSections, sectionsKey]);
  const shownPreviews = sectionQuery && sectionPreviews.data?.length === sections.length ? sectionPreviews.data.map((v, i) => ({ ...v, title: sections[i].title })) : null;
  // Names of hand-picked listings, paused ones too.
  const mine = useAsync((signal) => (maxSections > 0 ? marketApi.myListings(signal) : Promise.resolve(null)), [maxSections]);
  const nav = useAsync((signal) => (maxSections > 0 ? marketApi.nav(game, signal) : Promise.resolve(null)), [maxSections, game]);
  const coupons = useAsync((signal) => (allowed ? marketApi.coupons(signal) : Promise.resolve([] as SellerCoupon[])), [allowed]);
  // Public coupons can be announced (the one already chosen stays listed even if it was paused since).
  const announceable = (coupons.data ?? []).filter((c) => (ANNOUNCEABLE_SCOPES as readonly string[]).includes(c.scope) && (c.status === "active" || c.id === annCoupon));
  const [pins, setPins] = useState<ListingCard[] | null>(null);
  const [dropHidden, setDropHidden] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  if (!allowed) return <ShopShowcase name={seller.displayName} tagline={seller.bio.split("\n")[0]?.slice(0, 80) ?? ""} links={seller.shop?.links.length ?? 0} />;
  // The banner's link follows its section when sections move, and goes with it (edited in place: stays).
  const changeSections = (next: ShopSection[]) => {
    if (bannerLink?.kind === "section") {
      const found = next.indexOf(sections[bannerLink.index]);
      const index = found >= 0 ? found : next.length === sections.length ? bannerLink.index : -1;
      setBannerLink(index >= 0 ? { kind: "section", index } : null);
    }
    setSections(next);
  };
  const livePins = saved.data?.cards ?? [];
  const shownPins = pins ?? livePins;
  const hiddenPins = dropHidden ? [] : (saved.data?.ids ?? []).filter((id) => !livePins.some((p) => p.id === id));
  const run = async (work: () => Promise<MySeller>) => {
    setStatus("saving");
    try {
      onSaved(await work());
      setStatus("saved");
    } catch (err) {
      const fields = err instanceof ApiError ? ((err.details as { fields?: string[] } | undefined)?.fields ?? []) : [];
      setStatus(err instanceof ApiError && err.code === "blocked" ? "blocked" : fields.includes("links") ? "links" : fields.includes("announcement") ? "announcement" : fields.includes("sections") ? "sections" : fields.includes("bannerLink") ? "bannerLink" : "error");
    }
  };
  const chosen = announceable.find((c) => c.id === annCoupon);
  const chosenOff = chosen !== undefined && !couponWorksNow(chosen);
  // What the page would show, by the server's rule: nothing past its last day or with a coupon that doesn't work now.
  const previewAnnouncement: ShopAnnouncement | null =
    annText.trim() && !chosenOff && !(annUntil && annUntil < argentinaDate(new Date()))
      ? { text: annText.replace(/\s+/g, " ").trim(), until: annUntil || null, coupon: chosen ? { ...chosen, scope: chosen.scope as AnnounceableScope } : null }
      : null;
  // Where the banner would lead, by the server's rule: a section with cards, or a listing on sale.
  const shownSections = (shownPreviews ?? []).filter((x) => x.total > 0);
  const linkedSection = bannerLink?.kind === "section" ? shownPreviews?.[bannerLink.index] : undefined;
  const linkedListing = bannerLink?.kind === "listing" ? (mine.data ?? []).find((l) => l.id === bannerLink.id && l.status === "active" && l.quantity > 0) : undefined;
  const previewBannerTarget: ShopBannerTarget | null =
    linkedSection && linkedSection.total > 0
      ? { kind: "section", index: shownSections.indexOf(linkedSection), title: linkedSection.title }
      : linkedListing
        ? { kind: "listing", id: linkedListing.id, name: linkedListing.name }
        : null;
  const vacation = seller.vacationUntil && seller.vacationUntil >= argentinaDate(new Date()) ? new Date(`${seller.vacationUntil}T12:00:00`).toLocaleDateString(locale === "es" ? "es-AR" : "en-US", { day: "numeric", month: "long" }) : null;
  return (
    <section className="stack plan-card" aria-label={t("mkt.shop.title")}>
      <h2 className="small-title">{t("mkt.shop.title")}</h2>
      <fieldset className="stack-sm">
        <legend>{t("mkt.shop.announcement")}</legend>
        <label htmlFor="shop-announcement">{t("mkt.shop.announcementText")}</label>
        <input id="shop-announcement" type="text" maxLength={SHOP_ANNOUNCEMENT_MAX} value={annText} placeholder={t("mkt.shop.announcementPlaceholder")} onChange={(e) => setAnnText(e.target.value)} />
        <span className="field-hint">{t("mkt.shop.announcementHint", { max: SHOP_ANNOUNCEMENT_MAX })}</span>
        <label htmlFor="shop-announcement-coupon">{t("mkt.shop.announcementCoupon")}</label>
        <select id="shop-announcement-coupon" value={annCoupon ?? ""} onChange={(e) => setAnnCoupon(e.target.value ? Number(e.target.value) : null)}>
          <option value="">{t("mkt.shop.announcementNoCoupon")}</option>
          {announceable.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} · {couponOff(c)}
            </option>
          ))}
        </select>
        {coupons.data && announceable.length === 0 && <span className="field-hint">{t("mkt.shop.announcementNoCoupons")}</span>}
        {chosenOff && <span className="field-hint form-error">{t("mkt.shop.announcementCouponOff")}</span>}
        <label htmlFor="shop-announcement-until">{t("mkt.shop.announcementLast")}</label>
        <input id="shop-announcement-until" type="date" min={argentinaDate(new Date())} value={annUntil} onChange={(e) => setAnnUntil(e.target.value)} />
        <span className="field-hint">{t("mkt.shop.announcementRule")}</span>
      </fieldset>
      <ShopImage kind="banner" image={shop.banner} run={run} />
      {shop.banner && <BannerLinkPicker value={bannerLink} sections={sections} mine={mine.data ?? []} onChange={setBannerLink} />}
      <ShopImage kind="logo" image={shop.logo} run={run} />
      <div className="field">
        <label htmlFor="shop-tagline">{t("mkt.shop.tagline")}</label>
        <input id="shop-tagline" type="text" maxLength={SHOP_TAGLINE_MAX} value={tagline} placeholder={t("mkt.shop.taglinePlaceholder")} onChange={(e) => setTagline(e.target.value)} />
        <span className="field-hint">{t("mkt.shop.taglineHint", { max: SHOP_TAGLINE_MAX })}</span>
      </div>
      <fieldset className="stack-sm">
        <legend>{t("mkt.shop.accent")}</legend>
        <div className="accent-swatches">
          <label className="accent-swatch accent-none">
            <input type="radio" name="shop-accent" checked={accent === null} onChange={() => setAccent(null)} />
            <span>{t("mkt.shop.accentNone")}</span>
          </label>
          {Object.entries(SHOP_ACCENTS).map(([key, color]) => (
            <label key={key} className="accent-swatch" style={{ background: color }} title={t(`mkt.shop.accent.${key}` as never)}>
              <input type="radio" name="shop-accent" aria-label={t(`mkt.shop.accent.${key}` as never)} checked={accent === key} onChange={() => isShopAccent(key) && setAccent(key)} />
            </label>
          ))}
        </div>
        <span className="field-hint">{t("mkt.shop.accentHint")}</span>
      </fieldset>
      <div className="field">
        <label htmlFor="shop-about">{t("mkt.shop.about")}</label>
        <textarea id="shop-about" rows={6} maxLength={SHOP_ABOUT_MAX} value={about} onChange={(e) => setAbout(e.target.value)} />
        <span className="field-hint">{t("mkt.shop.aboutHint", { max: SHOP_ABOUT_MAX })}</span>
      </div>
      <fieldset className="stack-sm">
        <legend>{t("mkt.shop.links")}</legend>
        {links.map((l, i) => (
          <input
            key={i}
            aria-label={t("mkt.shop.linkN", { n: i + 1 })}
            type="text"
            inputMode="url"
            autoCapitalize="none"
            placeholder="instagram.com/tu_tienda"
            value={l}
            maxLength={300}
            onChange={(e) => setLinks(links.map((x, j) => (j === i ? e.target.value : x)))}
          />
        ))}
        <span className="field-hint">{t("mkt.shop.linksHint")}</span>
      </fieldset>
      {maxPins > 0 && saved.data && (
        <PinsEditor pins={shownPins} hidden={hiddenPins.length} max={maxPins} vacation={vacation} onChange={setPins} onDropHidden={() => setDropHidden(true)} />
      )}
      {maxSections > 0 && <SectionsEditor sections={sections} previews={shownPreviews} mine={mine.data ?? []} sets={(nav.data?.sets ?? []).map((x) => x.name)} onChange={changeSections} />}
      {status === "links" && <p className="form-error">{t("mkt.shop.linksError")}</p>}
      {status === "sections" && <p className="form-error">{t("sec.error")}</p>}
      {status === "bannerLink" && <p className="form-error">{t("mkt.shop.bannerLinkError")}</p>}
      {status === "announcement" && <p className="form-error">{t("mkt.shop.announcementError")}</p>}
      {status === "blocked" && <p className="form-error">{t("mkt.blockedApply")}</p>}
      {status === "error" && <p className="form-error">{t("error.generic")}</p>}
      {status === "saved" && <p className="notice">{t("mkt.shop.saved")}</p>}
      <div className="btn-row">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={status === "saving"}
          onClick={() =>
            void run(async () => {
              const result = await marketApi.saveSeller({
                about,
                tagline,
                accent,
                links: links.map((l) => l.trim()).filter(Boolean),
                announcement: annText.trim() ? { text: annText, couponId: annCoupon, until: annUntil || null } : null,
                ...(maxSections > 0 ? { sections } : {}),
                // A section taken out in this same save takes the banner's link with it.
                bannerLink: bannerLink?.kind === "section" && bannerLink.index >= sections.length ? null : bannerLink,
              });
              if (saved.data && (pins !== null || dropHidden)) {
                await marketApi.saveShopPins([...shownPins.map((p) => p.id), ...hiddenPins]);
                setPins(null);
                setDropHidden(false);
                saved.reload();
              }
              return result;
            })
          }
        >
          {t("mkt.shop.save")}
        </button>
        {pub.data && (
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setPreviewing(true)}>
            {t("mkt.shop.preview")}
          </button>
        )}
        <a className="btn btn-link btn-sm" href={`/vendedores/${seller.handle}`} target="_blank" rel="noopener">
          {t("mkt.shop.viewAsBuyer")} ↗
        </a>
      </div>
      {previewing && pub.data && (
        <ShopPreview
          seller={{
            ...pub.data,
            shop: { ...shop, about, tagline: tagline.replace(/\s+/g, " ").trim(), accent, links: links.map(parseShopLink).filter((l): l is ShopLink => l !== null) },
            pins: shownPins,
            announcement: previewAnnouncement,
            sections: (shownPreviews ?? []).filter((x) => x.total > 0),
            bannerTarget: previewBannerTarget,
          }}
          onClose={() => setPreviewing(false)}
        />
      )}
    </section>
  );
}

/** "El banner lleva a": nowhere, one of the shop's sections or one of its listings (saved with the rest). */
function BannerLinkPicker({ value, sections, mine, onChange }: { value: ShopBannerLink | null; sections: ShopSection[]; mine: MyListing[]; onChange: (v: ShopBannerLink | null) => void }) {
  const { t } = useI18n();
  const [picking, setPicking] = useState(false);
  const listing = value?.kind === "listing" ? mine.find((l) => l.id === value.id) : undefined;
  const choice = value?.kind === "section" ? `section:${value.index}` : value?.kind === "listing" || picking ? "listing" : "";
  return (
    <div className="field stack-sm">
      <label htmlFor="shop-banner-link">{t("mkt.shop.bannerLink")}</label>
      <select
        id="shop-banner-link"
        value={choice}
        onChange={(e) => {
          const v = e.target.value;
          setPicking(v === "listing");
          onChange(v.startsWith("section:") ? { kind: "section", index: Number(v.slice(8)) } : null);
        }}
      >
        <option value="">{t("mkt.shop.bannerLinkNone")}</option>
        {sections.map((x, i) => (
          <option key={i} value={`section:${i}`}>
            {t("mkt.shop.bannerLinkSection", { name: x.title.trim() || `${i + 1}` })}
          </option>
        ))}
        <option value="listing">{t("mkt.shop.bannerLinkListing")}</option>
      </select>
      {value?.kind === "listing" && (
        <p className="small">
          {t("mkt.shop.bannerLinkTo", { name: listing?.name ?? `#${value.id}` })}{" "}
          {listing && !(listing.status === "active" && listing.quantity > 0) && <span className="muted">{t("mkt.shop.bannerLinkHidden")}</span>}{" "}
          <button type="button" className="btn btn-link btn-sm" onClick={() => (setPicking(true), onChange(null))}>
            {t("mkt.shop.bannerLinkChange")}
          </button>
        </p>
      )}
      {picking && value === null && (
        <ListingPicker
          exclude={[]}
          label={t("mkt.shop.bannerLinkPick", { name: "{name}" })}
          verb={t("mkt.shop.bannerLinkPickShort")}
          onPick={(l) => {
            setPicking(false);
            onChange({ kind: "listing", id: l.id });
          }}
        />
      )}
      <span className="field-hint">{t("mkt.shop.bannerLinkHint")}</span>
    </div>
  );
}

/** The banner (4:1) or the logo (square): uploaded and removed right away, like a photo. */
function ShopImage({ kind, image, run }: { kind: "banner" | "logo"; image: string | null; run: (work: () => Promise<MySeller>) => Promise<void> }) {
  const { t } = useI18n();
  return (
    <div className="field">
      <span className="field-label">{t(`mkt.shop.${kind}`)}</span>
      {image && (kind === "banner" ? <img className="shop-banner" src={imageUrl(image)} alt="" width={800} height={200} /> : <img className="shop-logo" src={imageUrl(image)} alt="" width={88} height={88} />)}
      <div className="row-actions">
        <label className="btn btn-outline btn-sm">
          {t(image ? `mkt.shop.${kind}Change` : `mkt.shop.${kind}Upload`)}
          <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => e.target.files?.[0] && void run(() => marketApi.uploadShopImage(kind, e.target.files![0]))} />
        </label>
        {image && (
          <button type="button" className="btn btn-link btn-sm" onClick={() => void run(() => marketApi.deleteShopImage(kind))}>
            {t("mkt.shop.bannerRemove")}
          </button>
        )}
      </div>
      <span className="field-hint">{t(`mkt.shop.${kind}Hint`)}</span>
    </div>
  );
}

/**
 * "Destacadas en tu tienda" (plan perk): the listings that go first on the shop's page, in this order.
 * `hidden`: pinned ones buyers can't see now (paused, sold out); they keep their place until removed.
 */
export function PinsEditor({
  pins,
  hidden,
  max,
  vacation,
  onChange,
  onDropHidden,
}: {
  pins: ListingCard[];
  hidden: number;
  max: number;
  /** On vacation until this day: the pins are fine, the shop is closed. */
  vacation: string | null;
  onChange: (pins: ListingCard[]) => void;
  onDropHidden: () => void;
}) {
  const { t } = useI18n();
  const ars = useArs();
  const room = pins.length + hidden < max;
  const move = (i: number, by: -1 | 1) => {
    const next = [...pins];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    onChange(next);
  };
  return (
    <fieldset className="stack-sm">
      <legend>{t("mkt.shop.pins")}</legend>
      <span className="field-hint">{t("mkt.shop.pinsHint", { max })}</span>
      {vacation && <p className="notice small">{t("mkt.shop.pinsVacation", { date: vacation })}</p>}
      {pins.length > 0 && (
        <ol className="pin-list">
          {pins.map((p, i) => (
            <li key={p.id}>
              <span className="pin-name">
                {p.name} <span className="muted small">· {ars(p.priceArs)}</span>
              </span>
              <span className="row-actions">
                <button type="button" className="btn btn-link btn-sm" aria-label={t("mkt.shop.pinUp", { name: p.name })} disabled={i === 0} onClick={() => move(i, -1)}>
                  ↑
                </button>
                <button type="button" className="btn btn-link btn-sm" aria-label={t("mkt.shop.pinDown", { name: p.name })} disabled={i === pins.length - 1} onClick={() => move(i, 1)}>
                  ↓
                </button>
                <button type="button" className="btn btn-link btn-sm" aria-label={t("mkt.shop.pinRemove", { name: p.name })} onClick={() => onChange(pins.filter((x) => x.id !== p.id))}>
                  {t("mkt.shop.bannerRemove")}
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}
      {hidden > 0 && (
        <p className="muted small">
          {t("mkt.shop.pinsHidden", { count: hidden })}{" "}
          <button type="button" className="btn btn-link btn-sm" onClick={onDropHidden}>
            {t("mkt.shop.pinsDropHidden")}
          </button>
        </p>
      )}
      {room ? (
        <ListingPicker exclude={pins.map((p) => p.id)} label={t("mkt.shop.pinAdd", { name: "{name}" })} verb={t("mkt.shop.pinAddShort")} onPick={(l) => onChange([...pins, l])} />
      ) : (
        <p className="muted small">{t("mkt.shop.pinsFull", { max })}</p>
      )}
    </fieldset>
  );
}

/** "Vista previa": the shop as a buyer will see it, with the unsaved changes, on a phone or a computer. */
export function ShopPreview({ seller, onClose }: { seller: SellerPublic; onClose: () => void }) {
  const { t } = useI18n();
  const [width, setWidth] = useState<"phone" | "desktop">("phone");
  return (
    <Dialog title={t("mkt.shop.preview")} onClose={onClose} className="shop-preview-dialog">
      <div className="btn-row shop-preview-bar" role="group" aria-label={t("mkt.shop.previewWidth")}>
        {(["phone", "desktop"] as const).map((w) => (
          <button key={w} type="button" className={width === w ? "btn btn-primary btn-sm" : "btn btn-outline btn-sm"} aria-pressed={width === w} onClick={() => setWidth(w)}>
            {t(w === "phone" ? "mkt.shop.previewPhone" : "mkt.shop.previewDesktop")}
          </button>
        ))}
        <button type="button" className="btn btn-link btn-sm" onClick={onClose}>
          {t("mkt.shop.previewClose")}
        </button>
      </div>
      <p className="muted small">{t("mkt.shop.previewNote")}</p>
      <div className={`shop-preview-frame shop-preview-${width}`}>
        {/* Nothing in it leads away, by keyboard either: the changes aren't saved yet. */}
        <div inert>
          <ShopView seller={seller} preview />
        </div>
      </div>
    </Dialog>
  );
}

/** A search over the shop's own live listings, with a button to add each (pins, hand-picked sections). `label` has a {name} slot. */
function ListingPicker({ exclude, label, verb, onPick }: { exclude: number[]; label: string; verb: string; onPick: (l: ListingCard) => void }) {
  const { t } = useI18n();
  const ars = useArs();
  const [q, setQ] = useState("");
  const found = useAsync((signal) => marketApi.pinOptions(q.trim(), signal), [q]);
  const options = (found.data ?? []).filter((l) => !exclude.includes(l.id)).slice(0, 6);
  return (
    <>
      <input type="search" aria-label={t("mkt.shop.pinSearch")} placeholder={t("mkt.shop.pinSearch")} value={q} maxLength={80} onChange={(e) => setQ(e.target.value)} />
      {options.length > 0 && (
        <ul className="pin-options">
          {options.map((l) => (
            <li key={l.id}>
              <span className="pin-name">
                {l.name} <span className="muted small">· {ars(l.priceArs)}</span>
              </span>
              <button type="button" className="btn btn-outline btn-sm" aria-label={label.replace("{name}", l.name)} onClick={() => onPick(l)}>
                {verb}
              </button>
            </li>
          ))}
        </ul>
      )}
      {found.data && options.length === 0 && <p className="muted small">{t("mkt.shop.pinNone")}</p>}
    </>
  );
}

/** A template's title until the seller writes their own. */
const defaultTitle = (t: ReturnType<typeof useI18n>["t"], x: ShopSection, ars: (n: number) => string) =>
  (x.type === "under" ? t("sec.title.under", { price: ars(x.maxPriceArs ?? 10000) }) : x.type === "set" ? x.setName || t("sec.type.set") : t(`sec.type.${x.type}`)).slice(0, SHOP_SECTION_TITLE_MAX);

/**
 * "Secciones" (VMAX, up to SHOP_SECTIONS_MAX): each one a template with a title, shown under the pins.
 * `previews`: what each would show now (counts), from the server.
 */
export function SectionsEditor({
  sections,
  previews,
  mine,
  sets,
  onChange,
}: {
  sections: ShopSection[];
  previews: ShopSectionView[] | null;
  /** The shop's listings, for the names of hand-picked ones (paused included). */
  mine: MyListing[];
  sets: string[];
  onChange: (sections: ShopSection[]) => void;
}) {
  const { t } = useI18n();
  const ars = useArs();
  const update = (i: number, next: ShopSection) => onChange(sections.map((x, j) => (j === i ? next : x)));
  const move = (i: number, by: -1 | 1) => {
    const next = [...sections];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    onChange(next);
  };
  const retype = (x: ShopSection, type: ShopSectionType): ShopSection => {
    const next: ShopSection = { title: x.title, type };
    if (type === "under") next.maxPriceArs = x.maxPriceArs ?? 10000;
    if (type === "set") next.setName = x.setName ?? sets[0] ?? "";
    if (type === "manual") next.listingIds = x.listingIds ?? [];
    // A title the seller didn't change follows the template.
    if (!x.title || x.title === defaultTitle(t, x, ars)) next.title = defaultTitle(t, next, ars);
    return next;
  };
  const [cards, setCards] = useState<Record<number, ListingCard>>({});
  const nameOf = (id: number) => {
    const own = mine.find((l) => l.id === id);
    if (own) return own.status === "active" && own.quantity > 0 ? own.name : t("sec.notLive", { name: own.name });
    return cards[id]?.name ?? `#${id}`;
  };
  return (
    <fieldset className="stack-sm">
      <legend>{t("sec.title")}</legend>
      <span className="field-hint">{t("sec.hint", { max: SHOP_SECTIONS_MAX })}</span>
      {sections.map((x, i) => {
        const view = previews?.[i];
        return (
          <div key={i} className="shop-section-edit stack-sm" role="group" aria-label={t("sec.n", { n: i + 1 })}>
            <div className="row-actions">
              <strong>{t("sec.n", { n: i + 1 })}</strong>
              <button type="button" className="btn btn-link btn-sm" aria-label={t("sec.up", { n: i + 1 })} disabled={i === 0} onClick={() => move(i, -1)}>
                ↑
              </button>
              <button type="button" className="btn btn-link btn-sm" aria-label={t("sec.down", { n: i + 1 })} disabled={i === sections.length - 1} onClick={() => move(i, 1)}>
                ↓
              </button>
              <button type="button" className="btn btn-link btn-sm" aria-label={t("sec.remove", { n: i + 1 })} onClick={() => onChange(sections.filter((_, j) => j !== i))}>
                {t("mkt.shop.bannerRemove")}
              </button>
            </div>
            <label>
              {t("sec.type")}
              <select value={x.type} onChange={(e) => update(i, retype(x, e.target.value as ShopSectionType))}>
                {SHOP_SECTION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`sec.type.${type}`)}
                  </option>
                ))}
              </select>
            </label>
            <span className="field-hint">{t(`sec.what.${x.type}`, { days: SHOP_SECTION_NEWEST_DAYS, max: SHOP_SECTION_ITEMS })}</span>
            <label>
              {t("sec.name")}
              <input type="text" maxLength={SHOP_SECTION_TITLE_MAX} value={x.title} onChange={(e) => update(i, { ...x, title: e.target.value })} />
            </label>
            {x.type === "under" && (
              <label>
                {t("sec.maxPrice")}
                <input type="number" inputMode="numeric" min={1} step={500} value={x.maxPriceArs ?? ""} onChange={(e) => update(i, { ...x, maxPriceArs: Math.round(Number(e.target.value)) || undefined, title: x.title === defaultTitle(t, x, ars) ? defaultTitle(t, { ...x, maxPriceArs: Math.round(Number(e.target.value)) || undefined }, ars) : x.title })} />
              </label>
            )}
            {x.type === "set" && (
              <label>
                {t("sec.set")}
                <input type="text" list="shop-section-sets" maxLength={80} value={x.setName ?? ""} onChange={(e) => update(i, { ...x, setName: e.target.value, title: x.title === defaultTitle(t, x, ars) ? defaultTitle(t, { ...x, setName: e.target.value }, ars) : x.title })} />
              </label>
            )}
            {x.type === "manual" && (
              <>
                {(x.listingIds ?? []).length > 0 && (
                  <ol className="pin-list">
                    {(x.listingIds ?? []).map((id) => (
                      <li key={id}>
                        <span className="pin-name">{nameOf(id)}</span>
                        <button type="button" className="btn btn-link btn-sm" aria-label={t("mkt.shop.pinRemove", { name: nameOf(id) })} onClick={() => update(i, { ...x, listingIds: (x.listingIds ?? []).filter((y) => y !== id) })}>
                          {t("mkt.shop.bannerRemove")}
                        </button>
                      </li>
                    ))}
                  </ol>
                )}
                {(x.listingIds ?? []).length < SHOP_SECTION_ITEMS && (
                  <ListingPicker
                    exclude={x.listingIds ?? []}
                    label={t("sec.add", { name: "{name}" })}
                    verb={t("sec.addShort")}
                    onPick={(l) => {
                      setCards((c) => ({ ...c, [l.id]: l }));
                      update(i, { ...x, listingIds: [...(x.listingIds ?? []), l.id] });
                    }}
                  />
                )}
              </>
            )}
            {view && <p className={view.total ? "muted small" : "small form-error"}>{view.total ? t("sec.count", { count: view.total }) : t("sec.empty")}</p>}
          </div>
        );
      })}
      <datalist id="shop-section-sets">
        {sets.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      {sections.length < SHOP_SECTIONS_MAX && (
        <button type="button" className="btn btn-outline btn-sm" onClick={() => onChange([...sections, retype({ title: "", type: "newest" }, "newest")])}>
          {t("sec.addSection")}
        </button>
      )}
    </fieldset>
  );
}
